import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { ArrowLeftIcon, ReplyIcon, TrashIcon, UsersIcon, XIcon } from '@heroicons/react/solid'
import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'
import { useCableChannel } from '@/utils/cable'

import Loading from '@/shared/Loading'
import ScamWarning from '@/shared/ScamWarning'
import PrivatePostMessageForm from '@/pages/Posts/PrivatePostMessageForm'
import NotifyLevelMenu from '@/pages/Posts/NotifyLevelMenu'
import { BarButton, QUICK_REACTIONS, ReactionPicker, ReactionsRow, ReactionsModal } from '@/pages/Posts/ChatReactions'
import { EVERYONE, mentionTokensIn, paintMentions } from '@/pages/Posts/MentionPicker'

// One DM thread, Discord-style: header on top, the message list as the only
// thing that scrolls, composer pinned to the bottom.
//
// Scrolling is always the list's own scrollTop, never scrollIntoView. That
// call scrolls every scrollable ancestor too, which is what used to drag the
// whole page around each time a thread opened or a message was sent.

const PAGE_SIZE = 50
// Within this many px of the bottom counts as "reading the latest", and new
// messages follow. Further up, the reader is in history and stays put.
const FOLLOW_SLACK_PX = 120
// Start fetching older messages this close to the top.
const LOAD_EARLIER_PX = 200
// Discord folds a run of messages from one person under one header.
const GROUP_WINDOW_MS = 5 * 60 * 1000
// How far back a click on a reply quote will page for the original.
const JUMP_MAX_PAGES = 5

// On until closed, and back on the next page load. A fake-support DM works
// because it looks like it came from us, so the reader has to already know we
// don't do that by the time one arrives.
const SupportNotice = () => {
  const [closed, setClosed] = useState(false)
  if (closed) return null
  return <div className='shrink-0 flex items-center gap-2 bg-yellow-50 border-b border-yellow-200 px-4 py-1.5 text-xs text-yellow-800'>
    <p className='flex-1'>CCC will NEVER send account links through DMs. Anyone who does is a scammer. Please report them.</p>
    <button type='button' onClick={() => setClosed(true)} aria-label='Close' className='shrink-0 text-yellow-700 hover:text-yellow-900'>
      <XIcon className='h-4 w-4' />
    </button>
  </div>
}

const sameDay = (a, b) => a.toDateString() === b.toDateString()

const timeOf = (date) => date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

// "Today at 3:41 PM", "Yesterday at 9:02 AM", "3/14/2026 3:41 PM".
const stamp = (iso) => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const now = new Date()
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (sameDay(date, now)) return `Today at ${timeOf(date)}`
  if (sameDay(date, yesterday)) return `Yesterday at ${timeOf(date)}`
  return `${date.toLocaleDateString()} ${timeOf(date)}`
}

const groupedWithPrevious = (message, previous) => (
  !!previous &&
  !message.replyTo &&
  previous.userId === message.userId &&
  new Date(message.createdAt) - new Date(previous.createdAt) < GROUP_WINDOW_MS
)

// Counts come from broadcasts, which can't know whose reaction is whose, so
// `mine` is kept from whatever this client already believed.
const mergeCounts = (current, counts) => counts.map(c => ({
  ...c,
  mine: (current || []).find(r => r.key === c.key)?.mine || false
}))

// Drop a message, and turn quotes of it into "original deleted" so they
// don't keep its text or send a click hunting for it.
const withoutMessage = (list, id) => list
  .filter(m => m.id !== id)
  .map(m => (m.replyTo?.id === id ? { ...m, replyTo: { id, deleted: true } } : m))

const ReplyQuote = ({ replyTo, onJumpTo }) => {
  if (replyTo.deleted) {
    return <div className='mb-0.5 text-xs italic text-gray-400'>↱ The original message was deleted. It's gone. Poof.</div>
  }
  return <button
    type='button'
    onClick={() => onJumpTo(replyTo.id)}
    className='mb-0.5 flex items-center gap-1 max-w-full text-left text-xs text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
  >
    <span aria-hidden='true'>↱</span>
    <b className='shrink-0 font-semibold'>{replyTo.userDisplayName}</b>
    <span className='truncate'>{replyTo.preview}</span>
  </button>
}

const MessageRow = React.memo(({ message, grouped, isOwn, active, flashing, mentionsMe, knownUsernames, onActivate, onReply, onDelete, onToggleReaction, fetchReactors, onSeeAll, onJumpTo }) => {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  useEffect(() => { if (!active) setConfirmDelete(false) }, [active])

  // Touch has no hover, so a tap on the row toggles its action bar. Taps on
  // links and buttons inside it are theirs, not the row's.
  const onRowClick = (e) => {
    if (e.target.closest?.('a, button')) return
    if (String(window.getSelection?.() || '').length > 0) return
    onActivate(active ? null : message.id)
  }

  const barVisible = active || pickerOpen

  return <div
    id={`pm-${message.id}`}
    onClick={onRowClick}
    className={`group relative flex gap-3 px-4 transition-colors ${grouped ? 'py-0.5' : 'pt-3 pb-0.5'} ${
      flashing
        ? 'bg-yellow-100 dark:bg-yellow-500/20'
        : mentionsMe
          ? 'bg-amber-50 dark:bg-amber-500/10 border-l-2 border-amber-400'
          : active ? 'bg-gray-50 dark:bg-gray-700/50' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
    }`}
  >
    <div className='w-10 shrink-0'>
      {grouped
        ? <span className='hidden group-hover:block pt-1 text-[10px] leading-4 text-gray-400 text-right whitespace-nowrap'>{timeOf(new Date(message.createdAt))}</span>
        : <Link to={`/m/${message.userUsername}`}>
            <img className='h-10 w-10 rounded-full bg-gray-200 object-cover' src={message.publicImageUrl} alt='' />
          </Link>}
    </div>

    <div className='min-w-0 flex-1'>
      {message.replyTo && <ReplyQuote replyTo={message.replyTo} onJumpTo={onJumpTo} />}
      {!grouped && <div className='flex items-baseline gap-2'>
        <Link to={`/m/${message.userUsername}`} className='text-sm font-semibold text-gray-900 dark:text-white hover:underline'>{message.userDisplayName}</Link>
        <Link to={`/m/${message.userUsername}`} className='text-xs text-gray-500 dark:text-gray-400 hover:underline'>@{message.userUsername}</Link>
        <span className='text-xs text-gray-400'>{stamp(message.createdAt)}</span>
      </div>}
      <div
        className='text-sm text-gray-800 dark:text-gray-200 break-words prose prose-sm dark:prose-invert max-w-none prose-p:my-0'
        dangerouslySetInnerHTML={{ __html: paintMentions(message.body, knownUsernames) }}
      />
      <ReactionsRow message={message} onToggle={onToggleReaction} fetchReactors={fetchReactors} onSeeAll={onSeeAll} />
    </div>

    {/* Discord's pattern: actions hang off the row's top-right edge and appear
        on hover (or on tap, for touch), instead of sitting in the header and
        competing with the name for the eye. */}
    <div className={`absolute right-4 -top-3 z-20 items-center gap-0.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-1 py-0.5 shadow-sm ${
      barVisible ? 'flex' : 'hidden md:group-hover:flex'
    }`}>
      {confirmDelete
        ? <BarButton label='Really delete' tone='danger' onClick={() => onDelete(message.id)}>
            <span className='px-1 text-xs font-semibold'>Really delete? Cannot be undone.</span>
          </BarButton>
        : <>
            {QUICK_REACTIONS.map(r => (
              <BarButton key={r.key} label={r.key} onClick={() => onToggleReaction(message.id, r.key)}>
                <span className='text-base leading-none'>{r.icon}</span>
              </BarButton>
            ))}
            <ReactionPicker open={pickerOpen} setOpen={setPickerOpen} onPick={(key) => onToggleReaction(message.id, key)} />
            <BarButton label='Reply' onClick={() => onReply(message)}>
              <ReplyIcon className='h-4 w-4' />
            </BarButton>
            {isOwn && <BarButton label='Delete' tone='danger' onClick={() => setConfirmDelete(true)}>
              <TrashIcon className='h-4 w-4' />
            </BarButton>}
          </>}
    </div>
  </div>
})
MessageRow.displayName = 'MessageRow'

const PrivatePostShow = ({ postId, people, onShowPeople, isMobile, onLevelChange }) => {
  const [currentUser] = useGlobalState('currentUser')
  const [, setToast] = useGlobalState('toast')
  const { getRequest, putpostRequest } = useQuery()

  const [messages, setMessages] = useState([])
  const [project, setProject] = useState(null)
  const [ownerId, setOwnerId] = useState(null)
  const [notifyLevel, setNotifyLevel] = useState('all')
  const [loading, setLoading] = useState(true)
  const [loadingEarlier, setLoadingEarlier] = useState(false)
  const [replyTarget, setReplyTarget] = useState(null)
  const [activeId, setActiveId] = useState(null)
  const [flashId, setFlashId] = useState(null)
  const [reactionsTarget, setReactionsTarget] = useState(null)
  const [focusKey, setFocusKey] = useState(0)
  const [confirmLeave, setConfirmLeave] = useState(false)

  const listRef = useRef(null)
  const messagesRef = useRef([])
  messagesRef.current = messages
  const cursor = useRef({ startsAt: null, hasMore: false })
  const loadingEarlierRef = useRef(false)
  // What the next commit of `messages` should do to the scroll position:
  // 'bottom', { restore: priorScrollHeight } after a prepend, or null.
  const pendingScroll = useRef(null)
  // Responses for a thread we've since left are dropped, not applied.
  const liveId = useRef(postId)
  liveId.current = postId
  const reactorsCache = useRef(new Map())

  // Reaching for the keyboard on every thread switch is right on a desktop and
  // wrong on a phone, where it throws a keyboard over the thread you wanted to read.
  const focusComposer = useCallback(() => { if (!isMobile) setFocusKey(k => k + 1) }, [isMobile])

  const nearBottom = () => {
    const el = listRef.current
    return !el || el.scrollHeight - el.scrollTop - el.clientHeight < FOLLOW_SLACK_PX
  }

  // Before paint, so a restored position never flashes at the wrong spot.
  useLayoutEffect(() => {
    const el = listRef.current
    const action = pendingScroll.current
    pendingScroll.current = null
    if (!el || !action) return
    if (action === 'bottom') { el.scrollTop = el.scrollHeight; return }
    el.scrollTop = el.scrollHeight - action.restore + action.top
  }, [messages])

  const loadEarlier = useCallback(() => new Promise((resolve) => {
    if (loadingEarlierRef.current || !cursor.current.hasMore) { resolve(false); return }
    loadingEarlierRef.current = true
    setLoadingEarlier(true)
    const forId = postId
    getRequest(`/api/v3/posts/${postId}/post_messages`, { ends_at: cursor.current.startsAt, limit: PAGE_SIZE }, (err, jsonData) => {
      loadingEarlierRef.current = false
      setLoadingEarlier(false)
      if (err || liveId.current !== forId) { resolve(false); return }

      cursor.current = { startsAt: jsonData.startsAt, hasMore: !!jsonData.hasMore }
      const known = new Set(messagesRef.current.map(m => m.id))
      const older = jsonData.postMessages.filter(m => !known.has(m.id))
      const el = listRef.current
      pendingScroll.current = el ? { restore: el.scrollHeight, top: el.scrollTop } : null
      // Ahead of the render too, so a caller awaiting this (jumpTo) sees the
      // older page the moment the promise settles.
      messagesRef.current = [...older, ...messagesRef.current]
      setMessages(prev => [...older, ...prev])
      resolve(true)
    })
  }), [postId])

  // Open a thread: newest page, pinned to the bottom.
  useEffect(() => {
    setConfirmLeave(false)
    setMessages([])
    setProject(null)
    setReplyTarget(null)
    setActiveId(null)
    setLoading(true)
    cursor.current = { startsAt: null, hasMore: false }
    reactorsCache.current = new Map()
    const forId = postId

    getRequest(`/api/v3/posts/${postId}/post_messages`, { limit: PAGE_SIZE }, (err, jsonData) => {
      if (liveId.current !== forId) return
      setLoading(false)
      if (err) return

      cursor.current = { startsAt: jsonData.startsAt, hasMore: !!jsonData.hasMore }
      pendingScroll.current = 'bottom'
      // Merge, don't replace: anything that arrived live while this was in
      // flight is newer than the page and would otherwise vanish.
      setMessages(prev => {
        const known = new Set(jsonData.postMessages.map(m => m.id))
        return [...jsonData.postMessages, ...prev.filter(m => !known.has(m.id))]
      })
      setProject(jsonData.project?.id ? jsonData.project : null)
      setOwnerId(jsonData.ownerId)
      setNotifyLevel(jsonData.notifyLevel || 'all')
    })
    focusComposer()
  }, [postId])

  // A first page shorter than the pane can't be scrolled, so the scroll
  // handler would never ask for more. Keep loading until it fills or runs out.
  useEffect(() => {
    const el = listRef.current
    if (!el || loading || loadingEarlier) return
    if (cursor.current.hasMore && el.scrollHeight <= el.clientHeight) loadEarlier()
  }, [messages, loading, loadingEarlier])

  const onScroll = () => {
    const el = listRef.current
    if (el && el.scrollTop < LOAD_EARLIER_PX && !loading) loadEarlier()
  }

  // Up here rather than in the people panel: down there it sat under the
  // audio player's tab, and Closing Credits keeps Leave in the room header.
  const leaveThread = () => {
    putpostRequest(`/api/v3/posts/${postId}/leave`, 'POST', {}, () => {
      window.location.href = '/private_messages'
    })
  }

  // Optimistic: the menu closes on click, so waiting for the round trip would
  // show the old level for a beat. A refusal puts it back.
  const changeLevel = (level) => {
    const previous = notifyLevel
    setNotifyLevel(level)
    onLevelChange?.(postId, level)
    putpostRequest(`/api/v3/posts/${postId}/notify_level`, 'POST', { level }, (err) => {
      if (!err) return
      setNotifyLevel(previous)
      onLevelChange?.(postId, previous)
    })
  }

  const markRead = () => putpostRequest(`/api/v3/posts/${postId}/read`, 'POST', {}, () => {})

  useCableChannel('PrivateMessagesChannel', (data) => {
    if (String(data.postId) !== String(postId)) return

    if (data.event === 'created') {
      if (messagesRef.current.some(m => m.id === data.message.id)) return
      if (nearBottom() || data.message.userId === currentUser?.id) pendingScroll.current = 'bottom'
      setMessages(prev => [...prev, data.message])
      if (data.message.userId !== currentUser?.id) markRead()
    } else if (data.event === 'destroyed') {
      setMessages(prev => withoutMessage(prev, data.id))
    } else if (data.event === 'reaction') {
      reactorsCache.current.delete(data.id)
      setMessages(prev => prev.map(m => (m.id === data.id ? { ...m, reactions: mergeCounts(m.reactions, data.reactions) } : m)))
    }
  })

  const addPostMessage = useCallback((postMessage) => {
    pendingScroll.current = 'bottom'
    setReplyTarget(null)
    setMessages(prev => (prev.some(m => m.id === postMessage.id) ? prev : [...prev, postMessage]))
  }, [])

  const deleteMessage = useCallback((messageId) => {
    // Gone now: the row stayed clickable during the request, so a double click
    // used to send a second DELETE.
    setMessages(prev => withoutMessage(prev, messageId))
    setActiveId(null)
    putpostRequest(`/api/v3/post_messages/${messageId}`, 'DELETE', {}, () => {})
  }, [])

  const toggleReaction = useCallback((messageId, key) => {
    // Optimistic: the pill moves on click, and the server's answer (which
    // carries the real `mine` flags) replaces the guess.
    setMessages(prev => prev.map(m => {
      if (m.id !== messageId) return m
      const reactions = [...(m.reactions || [])]
      const i = reactions.findIndex(r => r.key === key)
      if (i === -1) {
        reactions.push({ key, count: 1, mine: true })
      } else if (reactions[i].mine) {
        reactions[i] = { ...reactions[i], count: reactions[i].count - 1, mine: false }
      } else {
        reactions[i] = { ...reactions[i], count: reactions[i].count + 1, mine: true }
      }
      return { ...m, reactions: reactions.filter(r => r.count > 0) }
    }))
    reactorsCache.current.delete(messageId)
    putpostRequest(`/api/v3/post_messages/${messageId}/toggle_reaction`, 'POST', { reaction: key }, (err, jsonData) => {
      if (err || !jsonData) return
      setMessages(prev => prev.map(m => (m.id === messageId ? { ...m, reactions: jsonData.reactions } : m)))
    })
  }, [])

  const fetchReactors = useCallback((messageId) => {
    const cached = reactorsCache.current.get(messageId)
    if (cached) return cached
    const promise = new Promise((resolve) => {
      getRequest(`/api/v3/post_messages/${messageId}/reactors`, {}, (err, data) => {
        // A failed lookup must not stick, or the hover card spins forever.
        if (err || !data) { reactorsCache.current.delete(messageId); resolve(null); return }
        resolve(data.reactions || [])
      })
    })
    reactorsCache.current.set(messageId, promise)
    return promise
  }, [])

  const showReactions = useCallback((messageId, key) => setReactionsTarget({ messageId, key }), [])

  const startReply = useCallback((message) => {
    setReplyTarget(message)
    setActiveId(null)
    setFocusKey(k => k + 1)
  }, [])

  const scrollToMessage = (messageId) => {
    const el = listRef.current
    const row = document.getElementById(`pm-${messageId}`)
    if (!el || !row) return false
    el.scrollTop = row.offsetTop - el.clientHeight / 2
    setFlashId(messageId)
    setTimeout(() => setFlashId(id => (id === messageId ? null : id)), 1500)
    return true
  }

  // The original may be pages back. Walk back a few pages looking for it,
  // then admit defeat rather than dragging in the whole history.
  const jumpTo = useCallback(async (messageId) => {
    for (let page = 0; page <= JUMP_MAX_PAGES; page += 1) {
      if (messagesRef.current.some(m => m.id === messageId)) {
        requestAnimationFrame(() => scrollToMessage(messageId))
        return
      }
      if (page === JUMP_MAX_PAGES || !(await loadEarlier())) break
    }
    setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
      <p className='text-sm font-medium dark:text-white text-gray-800'>Too far back</p>
      <p className='mt-1 text-sm dark:text-gray-300 text-gray-500'>I scrolled back as far as I'm willing to go and still couldn't find it. Keep scrolling up if you're braver than me.</p>
    </div>)
  }, [loadEarlier])

  const others = (people || []).filter(p => p.user?.id !== currentUser?.id)
  const title = others.map(p => p.userDisplayName).join(', ') || 'Just you, talking to yourself'
  // Usernames that resolve to someone in the thread, for painting pills.
  // @everyone only pings anyone from the owner, so only their copy gets a pill.
  const memberUsernames = useMemo(
    () => new Set((people || []).map(p => (p.userUsername || '').toLowerCase())),
    [people]
  )
  const ownerUsernames = useMemo(() => new Set([EVERYONE, ...memberUsernames]), [memberUsernames])
  // Discord's amber: messages aimed at you. Your own never are.
  const mentionsMe = (m) => {
    if (!currentUser || m.userId === currentUser.id) return false
    const tokens = mentionTokensIn(m.body)
    return tokens.includes((currentUser.username || '').toLowerCase()) || (tokens.includes(EVERYONE) && m.userId === ownerId)
  }
  const scamText = useMemo(
    () => messages.filter(m => m.userId !== currentUser?.id).map(m => m.body).join(' '),
    [messages, currentUser?.id]
  )

  return <div className='flex-1 min-h-0 flex flex-col bg-white dark:bg-gray-800'>
    <div className='shrink-0 h-14 flex items-center gap-3 px-3 sm:px-4 border-b border-gray-200 dark:border-gray-700'>
      {isMobile && <Link to='/private_messages' aria-label='Back to messages' className='text-gray-500 hover:text-gray-900 dark:hover:text-white'>
        <ArrowLeftIcon className='h-5 w-5' />
      </Link>}
      <div className='flex -space-x-2 shrink-0'>
        {others.slice(0, 3).map(p => (
          <img key={p.id} className='h-8 w-8 rounded-full ring-2 ring-white dark:ring-gray-800 bg-gray-200 object-cover' src={p.publicImageUrl} alt='' />
        ))}
      </div>
      <div className='min-w-0 flex-1'>
        <h2 className='text-sm font-semibold text-gray-900 dark:text-white truncate'>{title}</h2>
        {project && <a href={`/projects/${project.slug}`} className='block text-xs text-cccblue truncate hover:underline'>Project: {project.name}</a>}
      </div>
      <NotifyLevelMenu level={notifyLevel} onChange={changeLevel} />
      <button
        type='button'
        onClick={confirmLeave ? leaveThread : () => setConfirmLeave(true)}
        onBlur={() => setConfirmLeave(false)}
        className={`shrink-0 px-2.5 py-1 rounded-md text-xs font-semibold ${confirmLeave ? 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'}`}
      >
        {confirmLeave ? 'Really leave?' : 'Leave'}
      </button>
      {onShowPeople && <button type='button' onClick={onShowPeople} aria-label='People in this thread' className='p-1 text-gray-500 hover:text-gray-900 dark:hover:text-white'>
        <UsersIcon className='h-5 w-5' />
      </button>}
    </div>

    <SupportNotice />
    <div className='shrink-0 px-4 empty:hidden'><ScamWarning text={scamText} /></div>

    <div ref={listRef} onScroll={onScroll} className='relative flex-1 min-h-0 overflow-y-auto overscroll-contain pb-3'>
      {loadingEarlier && <div className='py-2'><Loading /></div>}
      {loading && <div className='py-6'><Loading /></div>}
      {!loading && messages.length === 0 && (
        <p className='px-4 py-10 text-sm text-center text-gray-500'>This message thread is oddly blank. I mean... why even start a thread if you're not going to say anything.</p>
      )}
      {messages.map((m, i) => (
        <MessageRow
          key={m.id}
          message={m}
          grouped={groupedWithPrevious(m, messages[i - 1])}
          isOwn={m.userId === currentUser?.id}
          active={activeId === m.id}
          flashing={flashId === m.id}
          mentionsMe={mentionsMe(m)}
          knownUsernames={m.userId === ownerId ? ownerUsernames : memberUsernames}
          onActivate={setActiveId}
          onReply={startReply}
          onDelete={deleteMessage}
          onToggleReaction={toggleReaction}
          fetchReactors={fetchReactors}
          onSeeAll={showReactions}
          onJumpTo={jumpTo}
        />
      ))}
    </div>

    <PrivatePostMessageForm
      postId={postId}
      addPostMessage={addPostMessage}
      replyTarget={replyTarget}
      onCancelReply={() => setReplyTarget(null)}
      focusKey={focusKey || undefined}
      people={people}
      me={currentUser?.id}
      ownerId={ownerId}
    />
    <ReactionsModal target={reactionsTarget} onClose={() => setReactionsTarget(null)} fetchReactors={fetchReactors} />
  </div>
}

export default PrivatePostShow

ReplyQuote.propTypes = { replyTo: PropTypes.object.isRequired, onJumpTo: PropTypes.func.isRequired }
MessageRow.propTypes = {
  message: PropTypes.object.isRequired,
  grouped: PropTypes.bool,
  isOwn: PropTypes.bool,
  active: PropTypes.bool,
  flashing: PropTypes.bool,
  mentionsMe: PropTypes.bool,
  knownUsernames: PropTypes.object,
  onActivate: PropTypes.func,
  onReply: PropTypes.func,
  onDelete: PropTypes.func,
  onToggleReaction: PropTypes.func,
  fetchReactors: PropTypes.func,
  onSeeAll: PropTypes.func,
  onJumpTo: PropTypes.func
}
PrivatePostShow.propTypes = {
  postId: PropTypes.string.isRequired,
  people: PropTypes.array,
  onShowPeople: PropTypes.func,
  isMobile: PropTypes.bool,
  onLevelChange: PropTypes.func
}
